import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type PreflightCheck } from "../api";
import { listState } from "../components/QueryState";

/**
 * Everything an operator would otherwise need a shell for.
 *
 * Each of these is also reachable with a bearer token, which is the rule the
 * whole project follows: the data plane is agent-native, and a control plane
 * only a browser could drive would undo that.
 */
export function Operations() {
  return (
    <article>
      <h1>Operations</h1>
      <Preflight />
      <Maintenance />
      <Tokens />
      <AuditTrail />
    </article>
  );
}

const MARK: Record<PreflightCheck["status"], string> = {
  ok: "✓",
  warn: "!",
  fail: "✗",
  skip: "–",
};

function Preflight() {
  // **A mutation, not a query.** Preflight writes a probe object to the artifact
  // store and asks the provider about the key, so it is a POST — and modelling
  // it as a query invited exactly the accidental re-execution (refetch on focus,
  // on reconnect, on mount) that made it worth moving off GET in the first place.
  const report = useMutation({ mutationFn: () => api.preflight() });

  return (
    <section className="attempt">
      <header>
        <strong>Readiness</strong>
        <span className="num">
          <button type="button" onClick={() => report.mutate()} disabled={report.isPending}>
            {report.isPending ? "Checking…" : "Run preflight"}
          </button>
        </span>
      </header>
      <p className="muted">
        Touches every dependency once, in the order a run touches them. Nothing
        calls a model, clones a repository or writes a run.
      </p>
      {report.error ? <p className="error">{report.error.message}</p> : null}
      {report.data ? (
        <div className="panel">
          <ul className="caps">
            {report.data.checks.map((check) => (
              <li key={check.name} className={check.status === "fail" ? "error" : ""}>
                {MARK[check.status]} <strong>{check.name}</strong>: {check.detail}
                {check.remedy ? <div className="muted">→ {check.remedy}</div> : null}
              </li>
            ))}
          </ul>
          <p className={report.data.ready ? "" : "error"}>
            {report.data.ready ? "Ready for a live run." : "Not ready."}
          </p>
        </div>
      ) : null}
    </section>
  );
}

function Maintenance() {
  const queries = useQueryClient();
  const reap = useMutation({
    mutationFn: api.reap,
    onSuccess: () => queries.invalidateQueries({ queryKey: ["runs"] }),
  });

  return (
    <section className="attempt">
      <header>
        <strong>Housekeeping</strong>
        <span className="num">
          <button type="button" onClick={() => reap.mutate()} disabled={reap.isPending}>
            {reap.isPending ? "Sweeping…" : "Run the reaper"}
          </button>
        </span>
      </header>
      <p className="muted">
        Fails attempts that were never picked up or have stopped reporting, closes
        their runs, and releases budget reservations orphaned by a pod that died
        between reserving and settling — without which a project's nightly budget
        quietly shrinks with every such death.
      </p>
      {reap.data ? (
        <div className="panel">
          <ul className="caps">
            {Object.entries(reap.data)
              .filter(([key]) => key !== "errors")
              .map(([key, value]) => (
                <li key={key}>
                  {key.replace(/_/g, " ")}: <strong>{String(value)}</strong>
                </li>
              ))}
          </ul>
          {reap.data.errors.length ? (
            <p className="error">{reap.data.errors.join("; ")}</p>
          ) : null}
        </div>
      ) : null}
      {reap.error ? <p className="error">{reap.error.message}</p> : null}
    </section>
  );
}

function Tokens() {
  const queries = useQueryClient();
  const tokens = useQuery({ queryKey: ["tokens"], queryFn: api.tokens });
  const [name, setName] = useState("");
  const [issued, setIssued] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createToken(name.trim()),
    onSuccess: (result) => {
      setIssued(result.token);
      setName("");
      queries.invalidateQueries({ queryKey: ["tokens"] });
    },
  });
  const revoke = useMutation({
    mutationFn: api.revokeToken,
    onSuccess: () => queries.invalidateQueries({ queryKey: ["tokens"] }),
  });

  return (
    <section className="attempt">
      <header>
        <strong>API tokens</strong>
      </header>
      <p className="muted">
        So the control plane is usable without a browser. Without one, a script's
        only option is raw SQL — which is how a wrong cost number came to be
        believed.
      </p>

      <form
        className="fields"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <label>
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ci"
            required
          />
        </label>
        <button type="submit" disabled={create.isPending}>
          Issue
        </button>
      </form>

      {issued ? (
        <div className="panel">
          {/* Shown once. Only the digest is stored, so a database dump yields
              nothing presentable — and neither does this page on reload. */}
          <p className="warn">Copy this now. Only its digest is stored.</p>
          <pre className="diff">{issued}</pre>
          <button type="button" onClick={() => setIssued(null)}>
            Done
          </button>
        </div>
      ) : null}

      {create.error ? <p className="error">Could not issue: {create.error.message}</p> : null}
      {/* Revocation is the one action here someone takes in a hurry, on a
          credential they believe is leaked. A refused DELETE that says nothing
          leaves the row looking live and reads as a click that did not
          register — so the operator retries instead of escalating. */}
      {revoke.error ? <p className="error">Could not revoke: {revoke.error.message}</p> : null}

      {listState(tokens, "API tokens", "No API tokens have been issued.") ?? (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Created</th>
              <th>Last used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(tokens.data ?? []).map((token) => (
              <tr key={token.id}>
                <td>{token.name}</td>
                <td className="muted">{token.created_at.slice(0, 10)}</td>
                <td className="muted">{token.last_used_at?.slice(0, 16) ?? "never"}</td>
                <td>
                  {token.revoked_at ? (
                    <span className="muted">revoked</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => revoke.mutate(token.id)}
                      disabled={revoke.isPending}
                    >
                      {revoke.isPending ? "Revoking…" : "Revoke"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function AuditTrail() {
  const entries = useQuery({ queryKey: ["audit"], queryFn: () => api.audit(50) });

  return (
    <section className="attempt">
      <header>
        <strong>Audit trail</strong>
      </header>
      <p className="muted">
        Append-only, enforced by a database trigger. It is the only property
        per-user sessions buy over the shared credential they replaced.
      </p>
      {/* An empty append-only log is a claim that nothing happened. A failed
          request must never be able to make that claim. */}
      {listState(entries, "the audit trail", "Nothing recorded yet.") ?? (
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>Action</th>
              <th>Outcome</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {(entries.data ?? []).map((entry, index) => (
              // eslint-disable-next-line react/no-array-index-key -- append-only, no id served
              <tr key={index}>
                <td className="muted">{entry.at.slice(0, 19).replace("T", " ")}</td>
                <td>
                  {entry.actor_label}
                  <span className="muted"> ({entry.actor_type})</span>
                </td>
                <td>
                  <code>{entry.action}</code>
                </td>
                <td className={entry.outcome === "refused" ? "warn" : ""}>{entry.outcome}</td>
                <td className="muted">{entry.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
